import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { apiClient } from '../../../helpers/apiHelper';
import {
  appointmentService,
  buildRealtimeExamPath,
  canStartExamNow,
  getApiErrorMessage,
  isAppointmentPassed,
  isInProgressPastEnd,
  type AppointmentRead,
} from '../../../helpers/api/appointment/appointment';
import { appointmentKeys } from '../../../helpers/api/appointment/appointment-query-keys';
import {
  useCreatePregnancy,
  type PregnancyData,
} from '../../../helpers/api/pregnancy/pregnancy';
import { useCreateStudyInit } from '../../../helpers/api/studies/studies';
import type {
  EnrichedWorklistItem,
  PregnancyResolution,
} from '../../../helpers/api/dicom/dicom';
import {
  derivePrenatalStudyCategory,
  isPregnancyActiveForExam,
  unwrapPatientDossier,
  type PregnancySnapshot,
} from '../../Pages/PatientManagement/PatientDetailsView';
import { buildPrenatalReportViewerUrl } from '../features/PrenatalExam/routing';
import PregnancyModal from '../WorklistSearch/PregnancyModal';
import StartExamChoiceModal, {
  type StartExamChoice,
} from './StartExamChoiceModal';

type PatientContext = {
  patient: Record<string, unknown>;
  pregnancy: PregnancySnapshot | null;
};

function normalizeStatus(status: string | undefined | null): string {
  return String(status || '')
    .toLowerCase()
    .replace(/^.*\./, '');
}

function friendlyStartExamError(
  err: unknown,
  t: (key: string, opts?: object) => string
): string {
  const raw = getApiErrorMessage(err, '');
  const lower = raw.toLowerCase();
  if (
    lower.includes('after the scheduled time has passed') ||
    lower.includes('time has passed')
  ) {
    return t('messages.startTooLate');
  }
  if (
    lower.includes('before the scheduled time') ||
    lower.includes('cannot be started before')
  ) {
    return t('messages.startTooEarly');
  }
  return raw || t('messages.startExamError');
}

function pickActivePregnancy(
  body: Record<string, unknown>,
  preferredId?: number | null
): PregnancySnapshot | null {
  const patient = (body.patient as Record<string, unknown> | undefined) ?? body;
  const candidates: PregnancySnapshot[] = [];

  const topLevel = body.pregnancy as PregnancySnapshot | null | undefined;
  if (topLevel && topLevel.id != null) {
    candidates.push(topLevel);
  }

  const embedded = patient.pregnancy as PregnancySnapshot | null | undefined;
  if (embedded && embedded.id != null) {
    candidates.push(embedded);
  }

  const list = (Array.isArray(body.pregnancies)
    ? body.pregnancies
    : Array.isArray(patient.pregnancies)
      ? patient.pregnancies
      : []) as PregnancySnapshot[];
  candidates.push(...list);

  if (preferredId != null) {
    const preferred = candidates.find(
      (p) => String(p.id) === String(preferredId)
    );
    if (preferred && isPregnancyActiveForExam(preferred)) {
      return preferred;
    }
  }

  return candidates.find((p) => isPregnancyActiveForExam(p)) ?? null;
}

function buildWorklistItemFromPatient(
  appointment: AppointmentRead,
  patient: Record<string, unknown>,
  pregnancy: PregnancySnapshot | null
): EnrichedWorklistItem {
  const lastName = String(
    patient.patient_last_name ?? appointment.patient_last_name ?? ''
  ).trim();
  const firstName = String(
    patient.patient_name ?? appointment.patient_name ?? ''
  ).trim();
  const patientNameForMwl =
    lastName || firstName ? `${lastName}^${firstName}` : 'Unknown^Patient';
  const birthRaw = patient.birth_date;
  const birthDate =
    birthRaw != null && birthRaw !== ''
      ? dayjs(birthRaw as string).format('YYYYMMDD')
      : '';
  const sex = String(patient.patient_sex ?? patient.sex ?? 'F')
    .trim()
    .toUpperCase()
    .slice(0, 1);

  return {
    mwl: {
      PatientName: patientNameForMwl,
      PatientID: String(appointment.patient_id),
      PatientBirthDate: birthDate,
      PatientSex: sex === 'M' || sex === 'F' ? sex : 'F',
    },
    resolution: {
      patient_exists: true,
      patient_uuid: String(appointment.patient_id),
      patient_id_internal: String(patient.patient_id ?? appointment.patient_id),
      active_pregnancy_exists: isPregnancyActiveForExam(pregnancy),
      pregnancy:
        pregnancy && pregnancy.id != null
          ? (pregnancy as unknown as PregnancyResolution)
          : null,
      study_exists: false,
      study_instance_uid: null,
    },
  };
}

async function fetchPatientContext(
  appointment: AppointmentRead
): Promise<PatientContext> {
  // `/with_related` was removed; dossier returns patient + active pregnancy.
  const response: unknown = await apiClient.get(
    `patients/${appointment.patient_id}/dossier`
  );
  const dossier = unwrapPatientDossier(response) as Record<string, unknown>;
  const patient =
    (dossier.patient as Record<string, unknown> | undefined) ?? {};
  const pregnancy = pickActivePregnancy(dossier, appointment.pregnancy_id);
  return { patient, pregnancy };
}

export function useStartAppointmentExam() {
  const { t } = useTranslation('Scheduling');
  const { t: tP } = useTranslation('Patient');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pendingAppointment, setPendingAppointment] =
    useState<AppointmentRead | null>(null);
  const [patientContext, setPatientContext] = useState<PatientContext | null>(
    null
  );
  const [pregnancyModalOpen, setPregnancyModalOpen] = useState(false);
  const [choiceModalOpen, setChoiceModalOpen] = useState(false);
  const [startingExamId, setStartingExamId] = useState<string | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const savingPregnancyRef = useRef(false);

  const { mutateAsync: createStudyInit } = useCreateStudyInit(
    undefined,
    undefined,
    false
  );

  const invalidateAppointments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
    queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
  }, [queryClient]);

  const resetFlow = useCallback(() => {
    savingPregnancyRef.current = false;
    setPendingAppointment(null);
    setPatientContext(null);
    setPregnancyModalOpen(false);
    setChoiceModalOpen(false);
    setStartingExamId(null);
    setIsLaunching(false);
  }, []);

  const handleClosePregnancyModal = useCallback(() => {
    setPregnancyModalOpen(false);
    // PregnancyModal always calls onClose after save; keep context for the exam choice step.
    if (savingPregnancyRef.current) return;
    resetFlow();
  }, [resetFlow]);

  const ensureCheckedIn = useCallback(
    async (app: AppointmentRead) => {
      const st = normalizeStatus(app.status);
      if (st === 'scheduled' || st === 'confirmed') {
        await appointmentService.checkIn(String(app.id));
        invalidateAppointments();
      }
    },
    [invalidateAppointments]
  );

  const persistExamMode = useCallback(
    async (
      app: AppointmentRead,
      mode: StartExamChoice,
      extra: Partial<AppointmentRead> = {}
    ) => {
      try {
        await appointmentService.update(String(app.id), {
          exam_mode: mode,
          ...extra,
        } as Partial<AppointmentRead>);
        invalidateAppointments();
      } catch {
        // Non-blocking for launch; Resume may still fall back to study_instance_uid.
      }
    },
    [invalidateAppointments]
  );

  const launchRealtime = useCallback(
    async (app: AppointmentRead, pregnancyId?: number | null) => {
      const resolvedPregnancyId = pregnancyId ?? app.pregnancy_id ?? null;
      // Persist mode BEFORE check-in — in_progress rows used to reject updates.
      await persistExamMode(app, 'realtime', {
        ...(resolvedPregnancyId != null &&
        (app.pregnancy_id == null ||
          Number(app.pregnancy_id) !== Number(resolvedPregnancyId))
          ? { pregnancy_id: Number(resolvedPregnancyId) }
          : {}),
      });
      await ensureCheckedIn(app);
      navigate(buildRealtimeExamPath({ ...app, exam_mode: 'realtime' }));
      resetFlow();
    },
    [ensureCheckedIn, navigate, persistExamMode, resetFlow]
  );

  const launchObExam = useCallback(
    async (app: AppointmentRead, pregnancy: PregnancySnapshot) => {
      const pregnancyId = Number(pregnancy.id);
      if (!Number.isFinite(pregnancyId) || !isPregnancyActiveForExam(pregnancy)) {
        message.error(tP('prenatalExam.messages.inactivePregnancy'));
        return;
      }

      await persistExamMode(app, 'ob', {
        ...(app.pregnancy_id == null || Number(app.pregnancy_id) !== pregnancyId
          ? { pregnancy_id: pregnancyId }
          : {}),
      });
      await ensureCheckedIn(app);

      // Reuse linked study when appointment already has one
      if (app.study_instance_uid) {
        navigate(buildPrenatalReportViewerUrl(String(app.study_instance_uid)));
        resetFlow();
        return;
      }

      const response = await createStudyInit({
        pregnancy_id: pregnancyId,
        appointment_id: String(app.id),
        study_mode: 'UPLOADER',
        category: derivePrenatalStudyCategory(pregnancy),
        type: 'OB',
        study_date: new Date().toISOString(),
        skip_serie_creation: true,
      });

      const studyInstanceUid = (response as {
        data?: { result?: { study_instance_uid?: string } };
      })?.data?.result?.study_instance_uid;

      if (!studyInstanceUid) {
        throw new Error('Study creation did not return a study_instance_uid.');
      }

      await persistExamMode(
        { ...app, study_instance_uid: studyInstanceUid },
        'ob',
        { study_instance_uid: studyInstanceUid }
      );

      void queryClient.invalidateQueries({ queryKey: ['pregnancy'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      void queryClient.invalidateQueries({ queryKey: ['recent-studies'] });
      void queryClient.invalidateQueries({ queryKey: ['paginated-exams'] });
      void queryClient.invalidateQueries({ queryKey: ['studies'] });

      navigate(buildPrenatalReportViewerUrl(studyInstanceUid));
      resetFlow();
    },
    [
      createStudyInit,
      ensureCheckedIn,
      navigate,
      persistExamMode,
      queryClient,
      resetFlow,
      tP,
    ]
  );

  const resolveSavedExamMode = (
    app: AppointmentRead
  ): StartExamChoice | null => {
    const mode = String(app.exam_mode || '')
      .trim()
      .toLowerCase();
    if (mode === 'ob' || mode === 'realtime') return mode;
    // Legacy: prenatal study already linked ⇒ OB path
    if (app.study_instance_uid) return 'ob';
    return null;
  };

  const openChoiceModal = useCallback(
    (app: AppointmentRead, context: PatientContext) => {
      setPendingAppointment(app);
      setPatientContext(context);
      setPregnancyModalOpen(false);
      setChoiceModalOpen(true);
      setStartingExamId(null);
    },
    []
  );

  const { mutateAsync: createPregnancyAsync } = useCreatePregnancy();

  const startExam = useCallback(
    async (app: AppointmentRead) => {
      const st = normalizeStatus(app.status);
      if (st === 'in_progress' && isInProgressPastEnd(app)) {
        message.warning(
          t('messages.examAlreadyFinished', {
            defaultValue:
              'This exam slot has ended. The appointment is marked as finished.',
          })
        );
        return;
      }
      if (st === 'scheduled' || st === 'confirmed') {
        if (isAppointmentPassed(app)) {
          message.warning(t('messages.startTooLate'));
          return;
        }
        if (!canStartExamNow(app)) {
          message.warning(t('messages.startTooEarly'));
          return;
        }
      }

      setStartingExamId(String(app.id));
      try {
        // Prefer fresh appointment row so Resume sees persisted exam_mode.
        let current = app;
        try {
          current = await appointmentService.get(String(app.id));
        } catch {
          current = app;
        }

        const context = await fetchPatientContext(current);
        setPendingAppointment(current);
        setPatientContext(context);

        if (!isPregnancyActiveForExam(context.pregnancy)) {
          setChoiceModalOpen(false);
          setPregnancyModalOpen(true);
          setStartingExamId(null);
          return;
        }

        const savedMode = resolveSavedExamMode(current);
        if (savedMode === 'realtime') {
          setIsLaunching(true);
          await launchRealtime(
            current,
            Number(context.pregnancy?.id) || null
          );
          return;
        }
        if (savedMode === 'ob' && context.pregnancy) {
          setIsLaunching(true);
          await launchObExam(current, context.pregnancy);
          return;
        }

        openChoiceModal(current, context);
        setStartingExamId(null);
      } catch (err: unknown) {
        message.error(friendlyStartExamError(err, t));
        resetFlow();
      }
    },
    [launchObExam, launchRealtime, openChoiceModal, resetFlow, t]
  );

  const pregnancyWorklistItem = useMemo((): EnrichedWorklistItem | null => {
    if (!pendingAppointment || !patientContext) return null;
    return buildWorklistItemFromPatient(
      pendingAppointment,
      patientContext.patient,
      patientContext.pregnancy
    );
  }, [pendingAppointment, patientContext]);

  const handleSavePregnancy = useCallback(
    async (pregnancyData: {
      lmp: string;
      edd: string;
      bpd: string;
      hc: string;
      ac: string;
      fl: string;
      crl: string;
      status?: string;
      grossesse_classee_a_risque?: boolean | null;
      risque_anemie?: boolean;
      risque_hta?: boolean;
      risque_diabete?: boolean;
      risque_cardiopathie?: boolean;
      risque_metrorragie?: boolean;
      risque_infection?: boolean;
      risque_preeclampsie?: boolean;
      risque_eclampsie?: boolean;
      risque_autre_details?: string;
      trimester: 'T1' | 'T2' | 'T3';
    }) => {
      // Capture before PregnancyModal calls onClose (which clears React state).
      const app = pendingAppointment;
      const context = patientContext;
      if (!app || !context) return;

      savingPregnancyRef.current = true;

      const payload: PregnancyData = {
        patient_id: String(app.patient_id),
        dating_method: 'LMP',
        last_menstrual_period: pregnancyData.lmp || undefined,
        estimated_due_date: pregnancyData.edd || undefined,
        preg_bpd: pregnancyData.bpd ? parseFloat(pregnancyData.bpd) : undefined,
        preg_hc: pregnancyData.hc ? parseFloat(pregnancyData.hc) : undefined,
        preg_ac: pregnancyData.ac ? parseFloat(pregnancyData.ac) : undefined,
        preg_fl: pregnancyData.fl ? parseFloat(pregnancyData.fl) : undefined,
        preg_crl: pregnancyData.crl ? parseFloat(pregnancyData.crl) : undefined,
        pregnancy_status: pregnancyData.status || 'Active',
        grossesse_classee_a_risque: pregnancyData.grossesse_classee_a_risque,
        risque_anemie: pregnancyData.risque_anemie === true,
        risque_hta: pregnancyData.risque_hta === true,
        risque_diabete: pregnancyData.risque_diabete === true,
        risque_cardiopathie: pregnancyData.risque_cardiopathie === true,
        risque_metrorragie: pregnancyData.risque_metrorragie === true,
        risque_infection: pregnancyData.risque_infection === true,
        risque_preeclampsie: pregnancyData.risque_preeclampsie === true,
        risque_eclampsie: pregnancyData.risque_eclampsie === true,
        risque_autre_details: pregnancyData.risque_autre_details?.trim() || null,
      };

      if (
        pregnancyData.bpd ||
        pregnancyData.hc ||
        pregnancyData.ac ||
        pregnancyData.fl ||
        pregnancyData.crl
      ) {
        payload.dating_method = 'ULTRASOUND';
      } else if (pregnancyData.edd && !pregnancyData.lmp) {
        payload.dating_method = 'EDD';
      }

      setIsLaunching(true);
      try {
        const response = await createPregnancyAsync(payload);
        const created =
          ((response as { data?: { data?: PregnancySnapshot } })?.data?.data ??
            (response as { data?: PregnancySnapshot })?.data ??
            response) as PregnancySnapshot;

        if (!isPregnancyActiveForExam(created)) {
          message.error(tP('prenatalExam.messages.inactivePregnancy'));
          resetFlow();
          return;
        }

        const pregnancyId = Number(created.id);
        if (Number.isFinite(pregnancyId)) {
          try {
            await appointmentService.update(String(app.id), {
              pregnancy_id: pregnancyId,
            } as Partial<AppointmentRead>);
            invalidateAppointments();
          } catch {
            // Non-blocking
          }
        }

        const nextContext: PatientContext = {
          patient: {
            ...context.patient,
            pregnancy: created,
          },
          pregnancy: created,
        };
        openChoiceModal(app, nextContext);
      } catch (err: unknown) {
        message.error(
          getApiErrorMessage(
            err,
            t('messages.createPregnancyError', {
              defaultValue: 'Unable to create pregnancy',
            })
          )
        );
        resetFlow();
      } finally {
        savingPregnancyRef.current = false;
        setIsLaunching(false);
      }
    },
    [
      createPregnancyAsync,
      invalidateAppointments,
      openChoiceModal,
      patientContext,
      pendingAppointment,
      resetFlow,
      t,
      tP,
    ]
  );

  const handleExamChoice = useCallback(
    async (choice: StartExamChoice) => {
      if (!pendingAppointment || !patientContext?.pregnancy) return;

      setIsLaunching(true);
      try {
        if (choice === 'realtime') {
          await launchRealtime(
            pendingAppointment,
            Number(patientContext.pregnancy.id) || null
          );
        } else {
          await launchObExam(pendingAppointment, patientContext.pregnancy);
        }
      } catch (err: unknown) {
        if (choice === 'ob') {
          message.error(tP('prenatalExam.messages.createError'));
        } else {
          message.error(friendlyStartExamError(err, t));
        }
        setIsLaunching(false);
      }
    },
    [launchObExam, launchRealtime, patientContext, pendingAppointment, t, tP]
  );

  const examFlowModals = (
    <>
      <PregnancyModal
        isOpen={pregnancyModalOpen && Boolean(pregnancyWorklistItem)}
        onClose={handleClosePregnancyModal}
        worklistItem={pregnancyWorklistItem}
        onSaveLocal={handleSavePregnancy}
        mode="create"
      />
      <StartExamChoiceModal
        isOpen={choiceModalOpen}
        onClose={resetFlow}
        onSelect={(choice) => void handleExamChoice(choice)}
        isBusy={isLaunching}
      />
    </>
  );

  return {
    startExam,
    startingExamId,
    isLaunching,
    examFlowModals,
  };
}
